import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export const MOTO_STATUS = ['Disponível', 'Vendida', 'Reservada', 'Consignada'];

// ── Auth simples (usuários na tabela) ─────────────────────────────────────────

export async function loginUsuario(usuario, senha) {
  const { data, error } = await supabase
    .from('usuarios')
    .select('*')
    .eq('usuario', usuario)
    .eq('senha', senha)
    .eq('ativo', true)
    .single();
  if (error || !data) return null;
  return data;
}

export async function listarUsuariosPendentes() {
  const { data } = await supabase.from('usuarios').select('*').eq('ativo', false).order('created_at');
  return data || [];
}

export async function listarUsuariosAtivos() {
  const { data } = await supabase.from('usuarios').select('*').eq('ativo', true).order('usuario');
  return data || [];
}

export async function aprovarUsuario(id) {
  await supabase.from('usuarios').update({ ativo: true }).eq('id', id);
}

export async function recusarUsuario(id) {
  await supabase.from('usuarios').delete().eq('id', id);
}

export async function solicitarAcesso(usuario, senha, nome) {
  const { error } = await supabase.from('usuarios').insert([{ usuario, senha, nome, ativo: false, perfil: 'usuario' }]);
  if (error) throw error;
}

// ── Motos ─────────────────────────────────────────────────────────────────────

export async function listarMotos() {
  const { data, error } = await supabase.from('motos').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function inserirMoto(moto) {
  const { data, error } = await supabase.from('motos').insert([moto]).select().single();
  if (error) throw error;
  return data;
}

export async function atualizarMoto(id, moto) {
  const { data, error } = await supabase.from('motos').update(moto).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function excluirMoto(id) {
  const { error } = await supabase.from('motos').delete().eq('id', id);
  if (error) throw error;
}

// ── Clientes ──────────────────────────────────────────────────────────────────

export async function listarClientes() {
  const { data, error } = await supabase.from('clientes').select('*').order('nome');
  if (error) throw error;
  return data || [];
}

export async function inserirCliente(cliente) {
  const { data, error } = await supabase.from('clientes').insert([cliente]).select().single();
  if (error) throw error;
  return data;
}

export async function atualizarCliente(id, cliente) {
  const { data, error } = await supabase.from('clientes').update(cliente).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function excluirCliente(id) {
  const { error } = await supabase.from('clientes').delete().eq('id', id);
  if (error) throw error;
}

// ── Financeiro ────────────────────────────────────────────────────────────────

export async function listarMovimentacoes() {
  const { data, error } = await supabase
    .from('financeiro')
    .select('*, motos(marca, modelo, placa)')
    .order('data', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function inserirMovimentacao(mov) {
  const { data, error } = await supabase.from('financeiro').insert([mov]).select().single();
  if (error) throw error;
  return data;
}

export async function atualizarMovimentacao(id, mov) {
  const { data, error } = await supabase.from('financeiro').update(mov).eq('id', id).select().single();
  if (error) throw error;
  return data;
}

export async function excluirMovimentacao(id) {
  const { error } = await supabase.from('financeiro').delete().eq('id', id);
  if (error) throw error;
}

// ── Fotos (Storage) ───────────────────────────────────────────────────────────

export async function uploadFoto(pasta, motoId, clienteId, file, tipo) {
  const prefixo = motoId ? `motos/${motoId}` : `clientes/${clienteId}`;
  const fileName = `${prefixo}/${tipo}/${Date.now()}_${file.name}`;
  const { error } = await supabase.storage.from('fotos-motos').upload(fileName, file);
  if (error) throw error;
  return fileName;
}

export async function uploadFotoMoto(motoId, file, tipo = 'galeria') {
  const fileName = `motos/${motoId}/${tipo}/${Date.now()}_${file.name}`;
  const { error } = await supabase.storage.from('fotos-motos').upload(fileName, file);
  if (error) throw error;
  const { data: foto, error: dbErr } = await supabase
    .from('fotos_moto')
    .insert([{ moto_id: motoId, caminho: fileName, tipo }])
    .select().single();
  if (dbErr) throw dbErr;
  return foto;
}

export async function uploadFotoCliente(clienteId, file, tipo = 'documento') {
  const fileName = `clientes/${clienteId}/${tipo}/${Date.now()}_${file.name}`;
  const { error } = await supabase.storage.from('fotos-motos').upload(fileName, file);
  if (error) throw error;
  const { data: foto, error: dbErr } = await supabase
    .from('fotos_cliente')
    .insert([{ cliente_id: clienteId, caminho: fileName, tipo }])
    .select().single();
  if (dbErr) throw dbErr;
  return foto;
}

export async function listarFotosMoto(motoId, tipo) {
  let q = supabase.from('fotos_moto').select('*').eq('moto_id', motoId);
  if (tipo) q = q.eq('tipo', tipo);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function listarFotosCliente(clienteId) {
  const { data, error } = await supabase.from('fotos_cliente').select('*').eq('cliente_id', clienteId);
  if (error) throw error;
  return data || [];
}

export function getUrlFoto(caminho) {
  const { data } = supabase.storage.from('fotos-motos').getPublicUrl(caminho);
  return data.publicUrl;
}

export async function excluirFotoMoto(id, caminho) {
  await supabase.storage.from('fotos-motos').remove([caminho]);
  await supabase.from('fotos_moto').delete().eq('id', id);
}

export async function excluirFotoCliente(id, caminho) {
  await supabase.storage.from('fotos-motos').remove([caminho]);
  await supabase.from('fotos_cliente').delete().eq('id', id);
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function fmtMoeda(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

export function fmtData(str) {
  if (!str) return '';
  try { return new Date(str + 'T00:00:00').toLocaleDateString('pt-BR'); } catch { return str; }
}
